import { useState } from "react";
import { ShieldCheck, Save, Lock, AlertCircle, AlertTriangle, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * HullInsuranceCard — Pro-only card at the top of the Insurance tab.
 * Captures the current active hull-insurance policy (provider + policy # +
 * renewal date) and surfaces the same red/amber compliance status used by
 * the Dashboard alert banner.
 *
 * Renewal date drives:
 *   - The sticky compliance banner on the Dashboard (see ComplianceAlertBanner
 *     in Dashboard.jsx)
 *   - The 14-day-out email reminder (retention.py _insurance_reminder_tick)
 *
 * Free-tier captains see a Pro upsell — the underlying field is silently
 * dropped from PATCH payloads server-side (routes/vessels.py) so a savvy
 * free user editing the payload directly can't bypass the gate either.
 */
export default function HullInsuranceCard({ vessel, isPro, canWrite, onVesselPatched, onGoPro }) {
    const [form, setForm] = useState({
        hull_insurance_provider: vessel.hull_insurance_provider || "",
        hull_insurance_policy_number: vessel.hull_insurance_policy_number || "",
        hull_insurance_renewal: vessel.hull_insurance_renewal || "",
    });
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState("");

    const set = (k, v) => { setForm((s) => ({ ...s, [k]: v })); setSaved(false); };

    const save = async () => {
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.patch(`/vessels/${vessel.id}`, form);
            onVesselPatched?.(data);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const status = computeInsuranceStatus(vessel.hull_insurance_renewal);

    if (!isPro) {
        return (
            <section data-testid="hull-insurance-upsell" className="bm-card" style={{
                padding: 18, marginBottom: 16,
                background: "linear-gradient(135deg, rgba(212,181,114,0.10) 0%, rgba(91,185,232,0.04) 100%)",
                border: "1px solid rgba(212,181,114,0.35)",
            }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(212,181,114,0.15)", color: "var(--bm-gold)" }}>
                        <ShieldCheck size={22} />
                    </div>
                    <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                            <strong style={{ color: "var(--bm-gold)" }}>Hull Insurance Renewal Tracker</strong>
                            <Lock size={12} color="var(--bm-gold)" />
                        </div>
                        <p style={{ margin: 0, fontSize: 13, color: "var(--bm-text-muted)", lineHeight: 1.55 }}>
                            Pro captains get a sticky dashboard banner 30 days before their policy renews — plus a friendly email reminder 14 days out so coverage never lapses. Upgrade to enable.
                        </p>
                    </div>
                    <button
                        data-testid="hull-insurance-upgrade-btn"
                        className="bm-btn bm-btn-gold"
                        onClick={onGoPro}
                        style={{ padding: "8px 16px", fontSize: 13, alignSelf: "center" }}
                    >
                        See Pro plans
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section data-testid="hull-insurance-card" className="bm-card" style={{ padding: 18, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                <div style={{ minWidth: 240 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <ShieldCheck size={18} color="var(--bm-gold)" /> Hull Insurance Policy
                    </h3>
                    <p style={{ margin: "6px 0 0", color: "var(--bm-text-muted)", fontSize: 13, lineHeight: 1.55, maxWidth: 640 }}>
                        Track your policy renewal so it never lapses. We&apos;ll flag it on your dashboard 30 days out and email you 14 days before.
                    </p>
                </div>
                <InsuranceStatusPill status={status} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
                <Field label="Provider">
                    <input
                        data-testid="hull-insurance-provider-input"
                        className="bm-input"
                        value={form.hull_insurance_provider}
                        onChange={(e) => set("hull_insurance_provider", e.target.value)}
                        placeholder="e.g. BoatUS, Progressive"
                        disabled={!canWrite}
                    />
                </Field>
                <Field label="Policy #">
                    <input
                        data-testid="hull-insurance-policy-input"
                        className="bm-input"
                        value={form.hull_insurance_policy_number}
                        onChange={(e) => set("hull_insurance_policy_number", e.target.value)}
                        placeholder="Optional"
                        disabled={!canWrite}
                    />
                </Field>
                <Field label="Renewal date" hint="We'll email you 14 days before this date.">
                    <input
                        data-testid="hull-insurance-renewal-input"
                        type="date"
                        className="bm-input"
                        value={form.hull_insurance_renewal}
                        onChange={(e) => set("hull_insurance_renewal", e.target.value)}
                        disabled={!canWrite}
                    />
                </Field>
            </div>

            {err && <div style={{ color: "var(--bm-bad)", marginTop: 10, fontSize: 13 }}>{err}</div>}

            {canWrite && (
                <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                        data-testid="hull-insurance-save-btn"
                        className="bm-btn bm-btn-gold"
                        onClick={save}
                        disabled={busy}
                        style={{ padding: "8px 18px", fontSize: 13 }}
                    >
                        <Save size={14} /> {busy ? "Saving…" : saved ? "Saved ✓" : "Save policy"}
                    </button>
                    {saved && (
                        <span data-testid="hull-insurance-saved" style={{ color: "var(--bm-good)", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Check size={13} /> Policy details saved
                        </span>
                    )}
                </div>
            )}
        </section>
    );
}

// Compute red/amber/green/none status from an ISO renewal date. Kept here so
// the parent Dashboard can import it for the ComplianceAlertBanner.
export function computeInsuranceStatus(renewalIso) {
    if (!renewalIso) return { level: "none", days: null, renewalIso: null };
    let renewalDate;
    try {
        renewalDate = new Date(renewalIso + "T00:00:00Z");
        if (Number.isNaN(renewalDate.getTime())) return { level: "none", days: null, renewalIso: null };
    } catch {
        return { level: "none", days: null, renewalIso: null };
    }
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const days = Math.round((renewalDate.getTime() - today.getTime()) / 86400000);
    if (days < 0) return { level: "red", days, renewalIso };
    if (days <= 30) return { level: "amber", days, renewalIso };
    return { level: "green", days, renewalIso };
}

function InsuranceStatusPill({ status }) {
    if (status.level === "red") {
        return (
            <span data-testid="hull-insurance-status-red" className="bm-tag" style={{ background: "rgba(255, 89, 89, 0.15)", borderColor: "rgba(255, 89, 89, 0.5)", color: "var(--bm-bad)", fontSize: 11, padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <AlertCircle size={11} /> Lapsed by {Math.abs(status.days)}d
            </span>
        );
    }
    if (status.level === "amber") {
        return (
            <span data-testid="hull-insurance-status-amber" className="bm-tag bm-tag-warn" style={{ background: "rgba(251, 191, 36, 0.12)", borderColor: "rgba(251, 191, 36, 0.5)", fontSize: 11, padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <AlertTriangle size={11} /> Renews in {status.days}d
            </span>
        );
    }
    if (status.level === "green") {
        return (
            <span data-testid="hull-insurance-status-green" className="bm-tag bm-tag-good" style={{ fontSize: 11, padding: "3px 8px" }}>
                {status.days}d out
            </span>
        );
    }
    return null;
}

function Field({ label, hint, children }) {
    return (
        <div>
            <div style={{ fontSize: 11, color: "var(--bm-text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {label}
                {hint && (
                    <div style={{ textTransform: "none", letterSpacing: 0, marginTop: 2, fontSize: 11, color: "var(--bm-text-dim)" }}>{hint}</div>
                )}
            </div>
            {children}
        </div>
    );
}
